import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaUtensils,
  FaBed,
  FaCamera
} from "react-icons/fa";

import { IoMdArrowDropright } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/banner5.jpg";
import imageDay1 from "../../assets/zanzibar1.jpg";
import imageDay2 from "../../assets/zanzibar2.jpg";
import imageDay3 from "../../assets/znz_bch.jpg";
import imageDay4 from "../../assets/ZANZIBAR5.jpg";
import imageDay5 from "../../assets/mnemba.jpg";

const hero = {
  kicker: "5 Days Nungwi Beach Escape",
  title: "Stone Town & Nungwi",
  subtitle:
    "A perfect blend of Zanzibar culture and Nungwi beach relaxation.",
  backgroundImage: imageHero,
  meta: ["5 Days", "Stone Town", "Nungwi Beach", "Ocean Activities"],
};

const overview = `Explore Stone Town's historic quarter before heading north to the famous beaches of Nungwi. Walk through centuries of culture, discover Zanzibar's architectural heritage, and unwind along one of the island's most beautiful coastlines. Enjoy optional activities such as snorkeling, dhow cruises, and visits to fishing villages while staying in carefully selected hotels.`;


const highlights = [
  {
    icon: FaMapMarkerAlt,
    title: "Stone Town History",
    description:
      "Visit the Old Fort, House of Wonders, and Sultan's Palace Museum with guided tours.",
    color: "#10b981",
  },
  {
    icon: FaStar,
    title: "Nungwi Beaches",
    description:
      "Soft white sand and calm turquoise waters ideal for swimming and relaxation.",
    color: "#f59e0b",
  },
  {
    icon: FaPlane,
    title: "Marine Activities",
    description:
      "Optional diving, snorkeling, dhow cruises, and fishing village visits.",
    color: "#3b82f6",
  },
  {
    icon: FaRegClock,
    title: "Island Leisure",
    description:
      "Relax with spa treatments, beach walks, and spectacular ocean sunsets.",
    color: "#8b5cf6",
  },
];

const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Route",
    description: "Stone Town > Spice Farms > Nungwi Beach.",
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "5 days combining culture and coastal relaxation.",
  },
  {
    icon: FaUtensils,
    title: "Meals",
    description: "Daily breakfast plus selected lunches and dinners.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "Neela Boutique Hotel & Toa Hotel & Spa.",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1",
    dayNum: "01",
    title: "Arrival in Zanzibar – Stone Town",
    image: imageDay1,
    body: "Arrive at Abeid Amani Karume International Airport where our representative welcomes you and transfers you to The Neela Boutique Hotel in Stone Town. After a short briefing about your upcoming adventure, enjoy free time to relax or explore the nearby historic streets and waterfront.",
    activities: "Meals Plan: Bed and Breakfast",
    tags: ["Arrival", "Stone Town", "Leisure"],
  },
  {
    dayLabel: "Day 2",
    dayNum: "02",
    title: "Stone Town Cultural Tour",
    image: imageDay2,
    body: "Explore the fascinating maze of Stone Town with a guided walk through historic streets. Highlights include the Old Fort, House of Wonders, and the former slave market site. Continue to the Sultan's Palace Museum before enjoying time along the waterfront.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["History", "Guided Tour", "Culture"],
  },
  {
    dayLabel: "Day 3",
    dayNum: "03",
    title: "Transfer to Nungwi Beach",
    image: imageDay3,
    body: "Travel north toward Nungwi passing spice farms and coastal villages. Upon arrival check in at Toa Hotel & Spa, a beachfront resort overlooking the Indian Ocean. The afternoon is free for beach relaxation or optional snorkeling and diving trips.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Transfer", "Beach", "Ocean"],
  },
  {
    dayLabel: "Day 4",
    dayNum: "04",
    title: "Nungwi Beach – Full Day",
    image: imageDay4,
    body: "Spend a full day enjoying Nungwi Beach. Optional activities include reef snorkeling, dhow cruises, paddleboarding, or visiting the nearby fishing village where traditional dhow boats are built. Alternatively relax by the beach or resort pool.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Snorkeling", "Village Tour", "Relaxation"],
  },
  {
    dayLabel: "Day 5",
    dayNum: "05",
    title: "Departure – Transfer to Airport",
    image: imageDay5,
    body: "Enjoy your final morning in Zanzibar with a relaxing beach walk or some last-minute souvenir shopping before your transfer to Abeid Amani Karume International Airport for your onward journey.",
    activities: "Meals Plan: Breakfast and Lunch",
    tags: ["Departure", "Beach Walk", "Souvenirs"],
  },
];

export default function NungwiBeachAdventure() {

  const appContext = useContext(AppContext);

  const theme = {
    primary: "#059669",
    primaryLight: "#10b981",
    accent: "#0d9488",
    bg: "#f8fafc",
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div style={{
      maxWidth: "1400px",
      margin: "0 auto",
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      color: theme.text,
      background: theme.bg
    }}>

      {/* HERO */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(3,8,16,0.85), rgba(5,95,105,0.75)), url(${imageHero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem"
        }}
      >
        <div style={{ maxWidth: "800px" }}>
          <span
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{
              background: "rgba(16,185,129,0.2)",
              border: "1px solid rgba(16,185,129,0.4)",
              color: "#6ee7b7",
              fontWeight: "600"
            }}
          >
            {hero.kicker}
          </span>

          <h1 style={{ fontSize: "clamp(2.5rem,6vw,4rem)", fontWeight: "700", marginBottom: "1rem" }}>
            {hero.title}
          </h1>

          <p style={{ fontSize: "1.3rem", marginBottom: "2rem" }}>
            {hero.subtitle}
          </p>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span key={i} style={{
                background: "rgba(255,255,255,0.15)",
                padding: "0.5rem 1.25rem",
                borderRadius: "50px"
              }}>
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>


      <div style={{ padding: "4rem 1.5rem" }}>

        {/* OVERVIEW */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.25rem)", fontWeight: "700", marginBottom: "1.5rem" }}>
            Experience Zanzibar
          </h2>

          <p style={{ fontSize: "1.1rem", lineHeight: 1.8, color: theme.textMuted }}>
            {overview}
          </p>

          {/* QUICK STATS */}
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center", marginTop: "2rem" }}>
            {details.map((d, i) => {
              const Icon = d.icon;
              return (
                <div key={i} style={{
                  background: theme.cardBg,
                  padding: "1rem 1.5rem",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  minWidth: "200px"
                }}>
                  <Icon size={22} style={{ color: theme.primary }} />
                  <div>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>


        {/* HIGHLIGHTS */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 style={{ fontWeight: "700", fontSize: "clamp(1.75rem,4vw,2.25rem)" }}>
              Trip Highlights
            </h2>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
            gap: "1.5rem"
          }}>
            {highlights.map((h, i) => {
              const Icon = h.icon;
              return (
                <article key={i} style={{
                  background: theme.cardBg,
                  padding: "1.75rem",
                  borderRadius: "16px",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)"
                }}>
                  <div style={{
                    width: "52px",
                    height: "52px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px",
                    background: `${h.color}15`,
                    color: h.color,
                    marginBottom: "1rem"
                  }}>
                    <Icon />
                  </div>

                  <h3 style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
                    {h.title}
                  </h3>

                  <p style={{ color: theme.textMuted }}>
                    {h.description}
                  </p>
                </article>
              )
            })}
          </div>
        </section>


        {/* ITINERARY */}
        <section>
          <div className="text-center mb-5">
            <h2 style={{ fontWeight: "700", fontSize: "clamp(1.75rem,4vw,2.25rem)" }}>
              Your 5-Day Journey
            </h2>
          </div>

          {itinerary.map((day, i) => (
            <article key={i} style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.3fr",
              gap: "2rem",
              marginBottom: "2rem",
              background: "white",
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow: "0 8px 22px rgba(0,0,0,0.08)"
            }}>
              <div style={{
                backgroundImage: `url(${day.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                minHeight: "260px"
              }} />

              <div style={{ padding: "2rem" }}>
                <h3 style={{ fontWeight: "700", marginBottom: "1rem" }}>
                  {day.title}
                </h3>

                <p style={{ color: theme.textMuted, marginBottom: "1rem", lineHeight: 1.7 }}>
                  {day.body}
                </p>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                  {day.tags.map((tag, idx) => (
                    <span key={idx} style={{
                      background: `${theme.primary}10`,
                      color: theme.primary,
                      padding: "0.35rem 0.8rem",
                      borderRadius: "40px",
                      fontSize: "0.8rem"
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>

                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  background: `${theme.accent}10`,
                  padding: "0.5rem 0.9rem",
                  borderRadius: "8px",
                  fontSize: "0.9rem"
                }}>
                  <FaUtensils size={14} />
                  {day.activities}
                </div>
              </div>
            </article>
          ))}
        </section>


        {/* CTA */}
        <section style={{
          marginTop: "4rem",
          textAlign: "center",
          padding: "3rem",
          borderRadius: "20px",
          background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`
        }}>
          <FaCamera size={40} style={{ color: theme.primary, marginBottom: "1rem" }} />

          <h3 style={{ fontWeight: "700", fontSize: "1.8rem", marginBottom: "1rem" }}>
            Ready for Your Nungwi Beach Escape?
          </h3>

          <p style={{ maxWidth: "600px", margin: "0 auto 2rem", color: theme.textMuted }}>
            Book your Zanzibar experience combining Stone Town culture and Nungwi beach relaxation.
          </p>

          <button
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
              border: "none",
              color: "white",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
            onClick={() => appContext.navTo({ item: 'plan-trip' })}
          >
            Book This Trip <IoMdArrowDropright size={20} />
          </button>
        </section>

      </div>
    </div>
  );
}